import { useTranslations } from 'next-intl';
import type { AxfoodBasicProductViewModel } from '@occ/api-client';
import { formatComparePrice, formatPromotionComparePrice } from '@helpers/productPromotion';
import { StyledProductInfo, StyledProductListItem } from './BuyAllProductsListItem.styles';
import { RefObject } from 'react';
import Text from '@atoms/Text/Text';
import ProductQuantityControl from '@components/molecules/ProductQuantityControl/ProductQuantityControl';
import ProductListItemImagePrice from '@molecules/ProductListItemImagePrice/ProductListItemImagePrice';

interface Props {
  product: AxfoodBasicProductViewModel;
  quantity?: number;
  quantityCallback?: (quantity: number) => void;
  ref?: RefObject<HTMLDivElement>;
}

const BuyAllProductsListItem = ({ product, quantity, quantityCallback, ref }: Props) => {
  const t = useTranslations('product');
  const promotion = product?.potentialPromotions?.[0];
  const hasPromotion = !!promotion;
  const lowestHistoricalPrice = promotion?.lowestHistoricalPrice;
  const redeemLimitLabel = promotion?.redeemLimitLabel || '';
  const formattedComparePrice = formatComparePrice(product, t);
  const formattedPromotionComparePrice = formatPromotionComparePrice(product, t);

  const isItemDisabled = product.outOfStock || typeof quantity === 'undefined' || quantity <= 0;
  const opacity = isItemDisabled ? 0.7 : 1;

  return (
    <StyledProductListItem data-testid="buy-all-products-list-item" ref={ref}>
      <ProductListItemImagePrice product={product} opacity={opacity} />

      <StyledProductInfo $opacity={opacity}>
        <Text align="left" type="subhead" size="small" ellipsis>
          {product.name}
        </Text>
        <Text align="left" type="detail" size="small" color="darkerGray" ellipsis>
          {product.productLine2}
        </Text>

        {!hasPromotion && (
          <Text align="left" type="detail" size="small" color="darkerGray" ellipsis>
            {formattedComparePrice}
          </Text>
        )}

        {hasPromotion && (
          <Text align="left" type="detail" size="small" color="red">
            {formattedPromotionComparePrice}
            {redeemLimitLabel && ` • ${redeemLimitLabel}`}
          </Text>
        )}
        {/* Lägsta 30-dgrspris */}
        {hasPromotion && lowestHistoricalPrice && (
          <Text align="left" type="detail" size="small" color="darkerGray">
            {t('price.lowestHistorical', { price: lowestHistoricalPrice.formattedValue ?? '' })}
          </Text>
        )}
      </StyledProductInfo>

      <ProductQuantityControl
        product={product}
        variant="cart"
        trackingSource="buy_all_products_component"
        quantity={quantity}
        quantityCallback={quantityCallback}
      />
    </StyledProductListItem>
  );
};

export default BuyAllProductsListItem;
